import React, { useCallback, useState, useEffect } from 'react';
import { Play, Square, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { LoadingSpinner } from '@/components/loading-spinner';
import { IconTile } from '../icon-tile';
import { getNodeIconColor } from '@/constants/node-styling';
import { NodeCategory } from '@/types/node-types';

interface HumanInterventionBannerProps {
  endTime: number; // Unix timestamp (ms) when countdown ends
  onResume: () => Promise<void>;
  onStop: () => Promise<void>;
  isStoppingWorkflow: boolean;
  nodeIcon?: string;
  nodeIconColor?: string;
  nodeType?: string;
  className?: string;
}

export const HumanInterventionBanner: React.FC<
  HumanInterventionBannerProps
> = ({
  endTime,
  onResume,
  onStop,
  isStoppingWorkflow,
  nodeIcon,
  nodeIconColor,
  nodeType,
  className,
}) => {
  // Derive countdown from endTime locally to avoid global store updates
  const [countdownSeconds, setCountdownSeconds] = useState(() => {
    const remainingMs = endTime - Date.now();
    return Math.max(0, Math.floor(remainingMs / 1000));
  });

  // Update countdown every second (local state only, no global store updates)
  useEffect(() => {
    const interval = setInterval(() => {
      const remainingMs = endTime - Date.now();
      const remaining = Math.max(0, Math.floor(remainingMs / 1000));
      setCountdownSeconds(remaining);
    }, 1000);

    return () => clearInterval(interval);
  }, [endTime]);

  const formatCountdown = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds
      .toString()
      .padStart(2, '0')}`;
  };

  const handleResume = useCallback(async () => {
    await onResume();
  }, [onResume]);

  const handleStop = useCallback(async () => {
    await onStop();
  }, [onStop]);

  // Get icon styling
  const iconColor =
    nodeType && nodeIconColor
      ? getNodeIconColor(nodeType as NodeCategory, nodeIconColor)
      : null;
  const useThemeColors = iconColor === null;

  return (
    <div
      className={cn(
        'flex flex-col gap-3 px-4 py-3 rounded-xl shadow-lg',
        'bg-card border border-border w-125',
        className,
      )}
      data-testid='human-intervention-banner'
    >
      {/* Header Row: Node Icon + Title */}
      <div className='flex items-center gap-2'>
        {/* Node Icon */}
        {nodeIcon && (
          <IconTile
            icon={nodeIcon}
            backgroundColor={iconColor || ''}
            iconColor={iconColor ? '#ffffff' : ''}
            size='small'
            className={useThemeColors ? 'bg-black dark:bg-white' : ''}
            iconClassName={
              useThemeColors ? 'size-5 text-white dark:text-black' : 'size-5'
            }
          />
        )}
        {/* Title */}
        <span className='font-semibold text-sm'>Human intervention</span>
      </div>

      {/* Description */}
      <span className='text-xs text-muted-foreground'>
        Workflow paused for approval. Review the outputs generated so far and
        decide whether to continue or stop. If you don&apos;t decide before the
        countdown ends, the workflow resolves this step on its own.
      </span>

      {/* Bottom Row: Countdown + Actions */}
      <div className='flex items-center justify-between gap-3'>
        {/* Countdown Display */}
        <div className='flex items-center gap-2 py-1.5 shrink-0'>
          <Clock className='size-6' />
          <span className='text-sm font-mono font-medium'>
            {formatCountdown(countdownSeconds)}
          </span>
        </div>

        {/* Actions */}
        <div className='flex items-center gap-2 shrink-0'>
          <Button
            variant='ghost'
            size='sm'
            className='h-9 gap-2'
            onClick={handleStop}
            disabled={isStoppingWorkflow}
          >
            {isStoppingWorkflow ? (
              <LoadingSpinner size='sm' />
            ) : (
              <Square className='size-4' />
            )}
            Stop Workflow
          </Button>
          <Button
            variant='default'
            size='sm'
            className='h-9 gap-2'
            onClick={handleResume}
            disabled={isStoppingWorkflow}
          >
            <Play className='size-4 text-white' />
            <span className='text-white'>Resume Workflow</span>
          </Button>
        </div>
      </div>
    </div>
  );
};
